import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { makeStyles } from 'tss-react/mui';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ErrorHandler, { SidebarIcon } from '../../Utils/handler';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useConfirm } from 'material-ui-confirm';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import sessionStoreUtil from '../../ApiSetup/Localstorage';
import { useDispatch } from 'react-redux';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import Assets from "../Common/Assets";
import { getAvailableAccess } from '../../Redux/Actions/action';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import TextLabel from '../Common/CommonTextLabel';
import { BucketURL } from '../../ApiSetup/Api';

const drawerWidth = 240;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(0)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(0)} + 1px)`,
    },
});

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: "#ffffff !important",
    backgroundImage: "none !important",
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const useStyles = makeStyles()((theme) => {
    return {
        non_active_tab: {
            "& .MuiTypography-root ": {
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: 400,
            }

        },
        active_tab: {
            "& .MuiTypography-root ": {
                color: "black",
                fontSize: "17px",
                lineHeight: "24.76px",
                fontWeight: 700,
            }
        },
        SideMainMenu: {
            color: "grey",
            fontWeight: 500,
            fontSize: "16px",
            marginLeft: "10px",
            letterSpacing: "2%",
            marginBottom: "12px",
            lineHeight: "21.83px",
        }
    }
})


function Layout({ children }) {
    // ---------- HOOK --------- 
    const theme = useTheme();
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const location = useLocation()
    const { classes } = useStyles();
    const confirmDialogue = useConfirm();
    const isAbove475px = useMediaQuery('(min-width:475px)');
    const user = sessionStoreUtil.getLSItem('userData') ? JSON.parse(sessionStoreUtil.getLSItem('userData')) : {}

    // --------- STATE -----------
    const [open, setOpen] = React.useState(false);
    const [businesEditdata, setBusinesEditdata] = React.useState({
        firstName: "",
        mobilenumber: "",
        email: "",
    });
    // const [availableAccess, setAvailableAccess] = React.useState()
    const [anchorEl, setAnchorEl] = React.useState(null);
    const availableAccess = useSelector(value => value?.allAvailableAccess?.AllAvailableAccessList)

    const viewedPageAccess = availableAccess?.filter((e) => e?.actionsType?.includes("View"))
    const openMenu = Boolean(anchorEl);



    // const activePage = "Dashboard"
    const activePage = viewedPageAccess?.find((e) => e.path === location?.pathname)?.pageName

    // --------- HANDLE CHANGE ----------------
    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onchangeBusinesdata = (e) => {
        const { name, value } = e.target
        setBusinesEditdata({ ...businesEditdata, [name]: value });
    }

    const handleEditClick = () => {
        document.getElementById('profilePicInput').click();
    };

    const handleLogout = () => {
        dispatch(showLoader(true));
        setTimeout(() => {
            sessionStoreUtil.removeLSItem("auth_token")
            navigate('/login')
            dispatch(showLoader(false));
        }, 1000)
    }

    const handleNavigation = (path, index) => {
        navigate(path);
        if (window.innerWidth < 991) {
            setOpen(false);
        }
    };

    // ---------- APi ------------ 
    React.useEffect(() => {
        dispatch(showLoader(true));
        dispatch(getAvailableAccess())
            .unwrap() // Automatically handles async response
            .catch((error) => console.error("error", error))
            .finally(() => dispatch(showLoader(false)));
    }, [dispatch]);

    React.useEffect(() => {
        const handleResize = () => {
            if (window?.innerWidth < 991) {
                setOpen(false);
            } else {
                setOpen(true);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // React.useEffect(() => {
    //     _getRoleList()
    // }, [])

    console.log(user, 'val')

    return (
        <>
            <Box sx={{ display: 'flex' }}>
                {/* <CssBaseline /> */}

                <AppBar position="fixed" open={open} sx={{ boxShadow: "none" }} >
                    <Box display={"flex"} alignItems={"center"}>
                        <IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerOpen} edge="end" sx={{ ...(open && { display: 'none' }), marginLeft: "15px" }}>
                            <MenuIcon sx={{ color: "black" }} />
                        </IconButton>
                        <Toolbar sx={{ justifyContent: "center", my: "3px", width: "100%" }} >
                            <Box display={"flex"} alignItems={"center"} justifyContent={"center"}>
                                {isAbove475px && (<Typography noWrap component="div" sx={{ fontSize: "30px", color: '#2f2481 !important', fontWeight: "800" }}> પાટીદાર પરિવાર સુરક્ષા કવચ (PPSK)</Typography>)}
                            </Box>
                        </Toolbar>
                        <Box alignItems={"center"} mr={"20px"}>
                            {isAbove475px &&
                                <Box display={"flex"} gap={"10px"} onClick={handleClick} sx={{ cursor: "pointer" }}>
                                    <Box sx={{ display: { xs: "none", sm: "block", } }}>
                                        <Box display={"flex"} flexDirection={"column"} alignItems={'flex-end'}>
                                            <TextLabel width={"max-content"} fontWeight={"500"} fontSize={"16px"} color={theme.palette.primary.main} title={user?.fullName} />
                                            <TextLabel width={"max-content"} fontWeight={"600"} fontSize={"14px"} color={theme.palette.primary.main} title={user?.loginRole} />
                                        </Box>
                                    </Box>
                                    {user?.avtar ? (
                                        <Avatar src={BucketURL + user?.avtar} alt={"image"} sx={{ height: 40, width: 40 }} />
                                    ) : (
                                        <Avatar style={{ height: "40px", width: "40px", fontSize: "16px" }} />
                                    )}
                                </Box>
                            }
                        </Box>
                    </Box>
                </AppBar >

                <Drawer variant="permanent" open={open} sx={{ '& .MuiDrawer-paper': { borderRight: '1px solid transparent', }, }}>
                    <DrawerHeader >
                        <Box display="flex" gap={1} justifyContent="space-between" alignItems="center">
                            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} sx={{ gap: "10px" }}>
                                <Assets src={"images/logo.svg"} height={"50px"} width={"50px"} />
                                <Typography noWrap component="div" sx={{ fontSize: "30px", color: '#2f2481 !important', fontWeight: "600" }}>PPSK</Typography>
                            </Box>
                            <Box>
                                <IconButton onClick={() => setOpen(!open)}>
                                    {!open ? ("") : (<ChevronLeftIcon sx={{ color: "black", }} />)}
                                </IconButton>
                            </Box>
                        </Box>
                    </DrawerHeader>

                    <List sx={{ mt: viewedPageAccess?.filter(menu => ["Dashboard", "Add Member", "Website"].includes(menu?.pageName))?.length > 0 ? "20px" : "0px" }}>
                        {/* Main Menu Section */}
                        {viewedPageAccess?.filter(menu => ["Dashboard", "Add Member", "Website"].includes(menu?.pageName))?.length > 0 && <Box className={classes.SideMainMenu}>MAIN MENU</Box>}
                        {viewedPageAccess?.filter(menu => ["Dashboard", "Add Member", "Website"].includes(menu?.pageName))?.map((menu, index) => {
                            return (
                                <ListItem key={index} disablePadding sx={{ alignItems: "center", display: "block", paddingLeft: "10px", }}>
                                    <Link to={menu?.path} style={{ textDecoration: "none", color: "inherit", }}>
                                        <ListItemButton onClick={() => handleNavigation(menu?.path, index)} sx={{ gap: "10px" }}>
                                            <ListItemIcon sx={{ minWidth: 0, mr: "5px", color: "black" }}>
                                                {
                                                    menu?.pageName === "Dashboard" ? (<Assets src={"images/dashboardHigh.svg"} height={"25px"} width={"25px"} />)
                                                        : menu?.pageName === "Add Member" ? (<Assets src={"images/addMemberr.svg"} height={"25px"} width={"25px"} />)
                                                            : menu?.pageName === "Website" ? (<Assets src={"images/elements.svg"} height={"25px"} width={"25px"} />) : <Assets src={"images/defaultIcon.svg"} height={"25px"} width={"25px"} /> // Default Icon
                                                }
                                            </ListItemIcon>
                                            <ListItemText primary={menu?.pageName} className={activePage === menu?.pageName ? classes.active_tab : classes.non_active_tab} />
                                        </ListItemButton>
                                    </Link>
                                </ListItem>
                            )
                        })}

                        {/* Claims Section */}
                        {viewedPageAccess?.filter(menu => ["Member Requests", "Documents", "Claims", "Member Search", "View Member", "Payment Request"].includes(menu?.pageName))?.length > 0 && <Box sx={{ mt: 5 }}>
                            <Box className={classes.SideMainMenu}>CLAIMS</Box>
                        </Box>}
                        {viewedPageAccess?.filter(menu => ["Member Requests", "Documents", "Claims", "Member Search", "View Member", "Payment Request"].includes(menu?.pageName))?.map((menu, index) => {
                            return (
                                <ListItem key={index} disablePadding sx={{ alignItems: "center", display: "block", paddingLeft: "10px", }}>
                                    <Link to={menu?.path} style={{ textDecoration: "none", color: "inherit", }}>
                                        <ListItemButton onClick={() => handleNavigation(menu?.path, index)} sx={{ gap: "10px" }}>
                                            <ListItemIcon sx={{ minWidth: 0, mr: "5px", color: "black" }}>
                                                {
                                                    menu?.pageName === "Member Requests" ? (<Assets src={"images/memberRequest.svg"} height={"25px"} width={"25px"} />) :
                                                        menu?.pageName === "Documents" ? (<Assets src={"images/documents.svg"} height={"25px"} width={"25px"} />)
                                                            : menu?.pageName === "Claims" ? (<Assets src={"images/claims.svg"} height={"25px"} width={"25px"} />)
                                                                : menu?.pageName === "Member Search" ? (<Assets src={"images/membersearch.svg"} height={"25px"} width={"25px"} />)
                                                                    : <Assets src={"images/defaultIcon.svg"} height={"25px"} width={"25px"} /> // Default Icon
                                                }
                                            </ListItemIcon>
                                            <ListItemText primary={menu?.pageName} className={activePage === menu?.pageName ? classes.active_tab : classes.non_active_tab} />
                                        </ListItemButton>
                                    </Link>
                                </ListItem>
                            )
                        })}

                        {/* Other Section */}
                        <Box sx={{ mt: 5 }}>
                            <Box className={classes.SideMainMenu}>OTHER</Box>
                        </Box>
                        {viewedPageAccess?.filter(menu => !["Dashboard", "Add Member", "Member Requests", "Documents", "Claims", "Member Search", "View Member", "Payment Request", "Website"].includes(menu?.pageName))?.map((menu, index) => {
                            return (
                                <ListItem key={index} disablePadding sx={{ alignItems: "center", display: "block", paddingLeft: "10px", }}                            >
                                    <Link
                                        to={menu?.path}
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                    >
                                        <ListItemButton
                                            onClick={() => handleNavigation(menu?.path, index)}
                                            sx={{ gap: "10px" }}
                                        >
                                            <ListItemIcon
                                                sx={{ minWidth: 0, mr: "5px", color: "black" }}
                                            >
                                                {
                                                    menu?.pageName === "Notifications" ? (<Assets src={"images/notifications.svg"} height={"25px"} width={"25px"} />)
                                                        : menu?.pageName === "Help Desk" ? (<Assets src={"images/helpDesk.svg"} height={"25px"} width={"25px"} />
                                                        ) : menu?.pageName === "Reports" ? (<Assets src={"images/reports.svg"} height={"25px"} width={"25px"} />
                                                        ) : menu?.pageName === "Settings" ? (<Assets src={"images/settings.svg"} height={"25px"} width={"25px"} />
                                                        ) : menu?.pageName === "Transaction History" ? (<Assets src={"images/transactionHistory.svg"} height={"25px"} width={"25px"} />)
                                                            : menu?.pageName === "All Transaction" ? (<Assets src={"images/allTransaction.svg"} height={"25px"} width={"25px"} />)
                                                                : <Assets src={"images/defaultIcon.svg"} height={"25px"} width={"25px"} /> // Default Icon
                                                }
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={menu?.pageName}
                                                className={
                                                    activePage === menu?.pageName
                                                        ? classes.active_tab
                                                        : classes.non_active_tab
                                                }
                                            />
                                        </ListItemButton>
                                    </Link>
                                </ListItem>
                            )
                        })}

                        {/* Logout Option */}
                        <ListItem disablePadding sx={{ alignItems: "center", display: "block", paddingLeft: "10px", }}>
                            <ListItemButton sx={{ gap: "10px", }}
                                onClick={() =>
                                    confirmDialogue({
                                        title: `Are you sure you want to Log Out?`,
                                        description: "This action will log you out of your account.",
                                        confirmationText: (
                                            <Typography sx={{ fontSize: { xs: "8px", sm: "14px", md: "16px" } }} >
                                                Yes, Log Out
                                            </Typography>
                                        ),
                                        cancellationText: (
                                            <Typography sx={{ fontSize: { xs: "8px", sm: "14px", md: "16px" } }}>
                                                NO, Stay Logged In
                                            </Typography>
                                        ),
                                        titleProps: { sx: { fontSize: "18px", fontWeight: 500, color: "#3E4094", padding: "5px 23px", }, },
                                    })
                                        .then(() => { handleLogout(); })
                                        .catch(err => { console.log(err); })
                                }
                            >
                                <ListItemIcon sx={{ minWidth: 0, mr: "5px", color: "black", }}                                >
                                    <Assets src={"images/logoutt.svg"} height={"25px"} width={"25px"} />
                                </ListItemIcon>
                                <ListItemText sx={{ "& .css-10hburv-MuiTypography-root ": { fontSize: "18px", lineHeight: "24px", fontWeight: "400 !important", }, }}>
                                    Logout
                                </ListItemText>
                            </ListItemButton>
                        </ListItem>
                    </List>


                </Drawer >

                <Box component="main"
                    sx={{
                        flexGrow: 1,
                        marginLeft: '1px ',
                        marginTop: '70px',
                        p: "30px 20px 16px 20px",
                        backgroundColor: "#f3f3f7",
                        height: 'calc(96vh - 90px)',
                        borderTopLeftRadius: '24px',
                        borderLeft: '1px solid #F586344D',
                        borderTop: '1px solid #F586344D',
                    }}>
                    {children}
                </Box>
            </Box >

        </>
    )
}

export default Layout
